import { ErrorMonitorToggle } from "@/components/ErrorMonitor";
import { ProtectedLayout } from "@/components/layout/ProtectedLayout";
import { LoggerPanel } from "@/components/LoggerPanel";
import { SentryErrorBoundary } from "@/components/SentryErrorBoundary";
import { TestSentry } from "@/components/TestSentry";
import { EnhancedToastProvider } from "@/components/ui/enhanced-toast";
import { apiConfig, config } from "@/config";
import { AuthProvider } from "@/contexts/AuthContext";
import { UserProvider } from "@/contexts/UserContext";
import { ActiveStudiesEnhanced } from "@/pages/ActiveStudiesEnhanced";
import { AuthCallback } from "@/pages/AuthCallback";
import { Login } from "@/pages/Login";
import { NewStudyModern } from "@/pages/NewStudyModern";
import { ResultsEnhanced } from "@/pages/ResultsEnhanced";
import { Settings } from "@/pages/Settings";
import TvBridgeAuth from "@/pages/TvBridgeAuth";


// ... existing imports ...

import { BaseError } from "@/utils/errors";
import { logger } from "@/utils/logger";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import React, { useEffect } from "react";
import {
    createBrowserRouter,
    Navigate,
    RouterProvider,
    useLocation,
    useNavigationType,
} from "react-router-dom";

// Lazy-loaded pages
const StudyComparisonPage = React.lazy(() => import("@/pages/StudyComparisonPage"));
const TraderMaxBoost = React.lazy(() => import("@/pages/TraderMaxBoost"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 1000, // Consider data fresh for 10 seconds by default
      retry: (failureCount, error) => {
        // Custom retry logic based on error type
        if (error instanceof BaseError) {
          return (
            error.isRecoverable() &&
            failureCount < apiConfig.retryConfig.maxRetries
          );
        }
        return failureCount < apiConfig.retryConfig.maxRetries;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: 1,
      onError: (error) => {
        logger.error("Mutation error", error);
      },
    },
  },
});

// Make queryClient available globally for debugging
if (config.features.debugMode) {
  (
    window as unknown as { __REACT_QUERY_CLIENT__: QueryClient }
  ).__REACT_QUERY_CLIENT__ = queryClient;
}

// Component to track route changes in Sentry
function RouteChangeTracker() {
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    // Add breadcrumb for route changes
    Sentry.addBreadcrumb({
      category: "navigation",
      message: `Navigated to ${location.pathname}`,
      level: "info",
      data: {
        from: document.referrer,
        to: location.pathname,
        search: location.search,
        hash: location.hash,
        navigationType,
      },
    });

    // Set transaction name based on route
    // In Sentry v8, transaction names are set automatically by the router integration
    // We can add a tag for navigation type instead
    Sentry.setTag("navigation.type", navigationType);
    Sentry.setTag("navigation.path", location.pathname);

    // Log navigation
    logger.info("Route changed", {
      pathname: location.pathname,
      search: location.search,
      navigationType,
    });
  }, [location, navigationType]);

  return null;
}

// Create router with future flags to fix React Router warnings
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouter(createBrowserRouter);
const router = sentryCreateBrowserRouter(
  [
    // Test-only route to render results without auth/ProtectedLayout
    ...(import.meta.env.MODE !== "production"
      ? [
          {
            path: "/test/results/:studyId",
            element: (
              <AuthProvider>
                <SentryErrorBoundary level="section">
                  <ResultsEnhanced />
                </SentryErrorBoundary>
              </AuthProvider>
            ),
          },
        ]
      : []),
    {
      path: "/login",
      element: (
        <AuthProvider>
          <Login />
        </AuthProvider>
      ),
    },
    {
      path: "/auth/callback",
      element: (
        <AuthProvider>
          <AuthCallback />
        </AuthProvider>
      ),
    },
    {
      path: "/tv-bridge/auth",
      element: <TvBridgeAuth />,
    },
    {
      path: "/",
      element: (
        <AuthProvider>
          <RouteChangeTracker />
          <UserProvider>
            <ProtectedLayout />
          </UserProvider>
        </AuthProvider>
      ),
      children: [
        {
          index: true,
          element: <Navigate to="/studies" replace />,
        },
        {
          path: "new-study",
          element: (
            <SentryErrorBoundary level="section">
              <NewStudyModern />
            </SentryErrorBoundary>
          ),
        },
        {
          path: "studies",
          element: (
            <SentryErrorBoundary level="section">
              <ActiveStudiesEnhanced />
            </SentryErrorBoundary>
          ),
        },
        {
          path: "active-studies",
          element: <Navigate to="/studies" replace />,
        },
        {
          path: "dashboard",
          element: <Navigate to="/studies" replace />,
        },
        {
          path: "results",
          element: (
            <SentryErrorBoundary level="section">
              <ResultsEnhanced />
            </SentryErrorBoundary>
          ),
        },
        {
          path: "results/compare",
          element: (
            <SentryErrorBoundary level="section">
              <React.Suspense fallback={<div className="flex items-center justify-center py-24"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>}>
                <StudyComparisonPage />
              </React.Suspense>
            </SentryErrorBoundary>
          ),
        },
        {
          path: "results/:studyId",
          element: (
            <SentryErrorBoundary level="section">
              <ResultsEnhanced />
            </SentryErrorBoundary>
          ),
        },
        {
          path: "settings",
          element: (
            <SentryErrorBoundary level="section">
              <Settings />
            </SentryErrorBoundary>
          ),
        },
        {
          path: "boost",
          element: (
            <SentryErrorBoundary level="section">
              <React.Suspense fallback={<div className="flex items-center justify-center py-24"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>}>
                <TraderMaxBoost />
              </React.Suspense>
            </SentryErrorBoundary>
          ),
        },
        {
          path: "admin",
          element: <Navigate to="/settings" replace />,
        },
        ...(import.meta.env.VITE_APP_ENV !== "production"
          ? [
              {
                path: "test/sentry",
                element: (
                  <SentryErrorBoundary level="section">
                    <TestSentry />
                  </SentryErrorBoundary>
                ),
              },
              // NOTE: This test route uses deprecated Supabase Realtime - production uses SSE via Redis Streams
            ]
          : []),
      ],
    },
  ],
  {
    // Enable future flags to remove warnings
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  },
);

function App() {
  const handleGlobalError = (error: Error, errorInfo: React.ErrorInfo) => {
    logger.error("Global error boundary triggered", {
      error:
        error instanceof BaseError
          ? error.toJSON()
          : { message: error.message, stack: error.stack },
      errorInfo,
    });
  };

  return (
    <SentryErrorBoundary level="page" showDialog onError={handleGlobalError}>
      <QueryClientProvider client={queryClient}>
        <EnhancedToastProvider>
          <RouterProvider router={router} />
          {config.features.loggerPanel && <LoggerPanel />}
          <ErrorMonitorToggle />
        </EnhancedToastProvider>
        {config.features.reactQueryDevtools && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </QueryClientProvider>
    </SentryErrorBoundary>
  );
}

export default App;
